import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Alert from 'shared/components/UC/Alert';
import Badge, { BadgeProps } from 'shared/components/UC/Badge';
import Button from 'shared/components/UC/Button';
import Modal from 'shared/components/UC/Modal';
import ErrorState from 'shared/components/ErrorState';
import Toast from 'shared/components/Toast';
import { useUser } from '../contexts/AuthContext';
import applicationService, {
  ApplicationServiceError,
} from '../services/applicationService';
import { FormTemplateWithSections, SectionBasedApplication } from '../types';

import styles from './DynamicApplicationList.module.css';

const CREATION_LABEL = 'Creación de borrador';
const SUBMISSION_OR_EDIT_LABEL = 'Envío / Última edición';

const statusBadge = (status: string): Pick<BadgeProps, 'label' | 'variant'> => {
  switch (status) {
    case 'draft':        return { label: 'Borrador',        variant: 'secondary' };
    case 'selected':     return { label: 'Adjudicada',      variant: 'success' };
    case 'not_selected': return { label: 'No seleccionada', variant: 'danger' };
    case 'under_review': return { label: 'En revisión',     variant: 'info' };
    default:             return { label: 'Enviada',         variant: 'info' };
  }
};

const DynamicApplicationList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const [applications, setApplications] = useState<SectionBasedApplication[]>(
    []
  );
  const [formTemplates, setFormTemplates] = useState<
    FormTemplateWithSections[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [applicationsError, setApplicationsError] = useState<string | null>(
    null
  );
  const [toast, setToast] = useState<{
    message: string;
    type: 'error' | 'success' | 'warning' | 'info';
  } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [reuseModal, setReuseModal] = useState<{ templateId: string } | null>(
    null
  );
  const [selectedSourceId, setSelectedSourceId] = useState<string>('');

  const [closedContestsOpen, setClosedContestsOpen] = useState(false);
  const [closedTemplates, setClosedTemplates] = useState<
    FormTemplateWithSections[]
  >([]);

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleOpenClosedContests = async () => {
    const templates = await applicationService.getClosedTemplates();
    setClosedTemplates(templates);
    setClosedContestsOpen(true);
  };

  // Get role-based application URL
  const getApplicationUrl = (applicationId: string) => {
    if (!user) return `/applications/${applicationId}`;
    return `/${user.role}/applications/${applicationId}`;
  };

  const fetchTemplates = useCallback(async () => {
    try {
      setTemplatesError(null);
      const templatesData = await applicationService.getActiveTemplates();
      setFormTemplates(templatesData);
    } catch (err) {
      console.error('Error fetching templates:', err);
      const errorMessage =
        err instanceof ApplicationServiceError
          ? err.message
          : 'Error desconocido al cargar concursos';
      setTemplatesError(errorMessage);
      setToast({ message: errorMessage, type: 'error' });
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      setApplicationsError(null);
      const applicationsData = await applicationService.getMyApplications(
        1,
        50
      );
      setApplications(applicationsData.data || []);
    } catch (err) {
      console.error('Error fetching applications:', err);
      const errorMessage =
        err instanceof ApplicationServiceError
          ? err.message
          : 'Error desconocido al cargar postulaciones';
      setApplicationsError(errorMessage);
      setToast({ message: errorMessage, type: 'error' });
    }
  }, []);

  useEffect(() => {
    if (!openDropdownId) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as Element).closest('[data-dropdown]')) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openDropdownId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchTemplates(), fetchApplications()]);
      setLoading(false);
    };

    fetchData();
  }, [fetchTemplates, fetchApplications]);

  const handleCreateFromSource = async () => {
    if (!selectedSourceId || !reuseModal) return;
    try {
      const newApp = await applicationService.createApplicationFromSource(
        reuseModal.templateId,
        selectedSourceId
      );
      setReuseModal(null);
      navigate(getApplicationUrl(newApp.id));
    } catch (err) {
      console.error('Error creating application from source:', err);
      setToast({ message: 'Error al crear la postulación.', type: 'error' });
    }
  };

  const createNewApplication = async (templateId: string) => {
    if (!user?.userProfile?.canApply) {
      setToast({
        message:
          'No tienes permisos para crear una postulación en este momento.',
        type: 'warning',
      });
      return;
    }

    try {
      const newApplication =
        await applicationService.createApplication(templateId);
      navigate(getApplicationUrl(newApplication.id));
    } catch (err) {
      console.error('Error creating application:', err);
      setToast({
        message:
          'Error al crear la postulación. Por favor, inténtalo de nuevo.',
        type: 'error',
      });
    }
  };

  const downloadPdf = async (applicationId: string) => {
    try {
      const blob =
        await applicationService.downloadApplicationPdf(applicationId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `application-${applicationId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setToast({
        message: 'Error al descargar el PDF. Por favor, inténtalo de nuevo.',
        type: 'error',
      });
    }
  };

  const deleteApplication = async (applicationId: string) => {
    if (
      !window.confirm(
        '¿Estás seguro de que quieres eliminar esta postulación? Esta acción no se puede deshacer.'
      )
    ) {
      return;
    }

    try {
      setDeleting(applicationId);
      await applicationService.deleteApplication(applicationId);

      // Remove the application from the list
      setApplications((prev) => prev.filter((app) => app.id !== applicationId));
      setToast({
        message: 'Postulación eliminada exitosamente',
        type: 'success',
      });
    } catch (err) {
      console.error('Error deleting application:', err);
      setToast({
        message:
          'Error al eliminar la postulación. Por favor, inténtalo de nuevo.',
        type: 'error',
      });
    } finally {
      setDeleting(null);
    }
  };

  const showToast = toast !== null;

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Cargando...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <section className={styles.templatesSection}>
        <h2 className={styles.sectionTitle}>Concursos abiertos</h2>
        {!!user?.userProfile?.canApply === false && (
          <Alert variant="warning" className={styles.restrictionAlert}>
            No cumples con los requisitos para crear postulaciones en este momento.{' '}
            {user?.userProfile?.platformMessage || (
              <>
                Revisa los requisitos en{' '}
                <a href="https://avanza.uc.cl/" target="_blank" rel="noopener noreferrer">
                  https://avanza.uc.cl/
                </a>{' '}
                o contacta a <a href="mailto:avanza.uc@uc.cl">avanza.uc@uc.cl</a> para más
                información.
              </>
            )}
          </Alert>
        )}
        {templatesError ? (
          <ErrorState
            title="Error al cargar concursos"
            message={templatesError}
            onRetry={fetchTemplates}
            retryLabel="Reintentar"
          />
        ) : formTemplates.length === 0 && !templatesError ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyMessage}>
              No hay concursos abiertos en este momento.
            </p>
          </div>
        ) : (
          <table className={styles.contestsTable}>
            <thead>
              <tr>
                <th>Concurso</th>
                <th>Fecha de cierre</th>
                <th>Bases</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {formTemplates.map((template) => {
                const deadlineText = template.deadline
                  ? new Date(template.deadline).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })
                  : '—';
                return (
                  <tr key={template.id}>
                    <td>
                      <div className={styles.contestNameCol}>
                        <h3 className={styles.contestTitle}>
                          {template.name}
                          {template.year ? ` ${template.year}` : ''}
                        </h3>
                      </div>
                    </td>
                    <td data-label="Fecha de cierre">
                      <div className={styles.deadlineCol}>
                        <span className={styles.contestDeadlineDate}>
                          {deadlineText}
                        </span>
                      </div>
                    </td>
                    <td data-label="Bases">
                      <div className={styles.basesCol}>
                        <Button
                          variant="text"
                          size="sm"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = '/templates/Bases_Avanza_UC.pdf';
                            link.download = 'Bases_Avanza_UC.pdf';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          <i className="uc-icon icon-size--sm">download</i>
                          Español
                        </Button>
                        <Button
                          variant="text"
                          size="sm"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = '/templates/Bases_Avanza_UC_EN.pdf';
                            link.download = 'Bases_Avanza_UC_EN.pdf';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          <i className="uc-icon icon-size--sm">download</i>
                          Inglés
                        </Button>
                      </div>
                    </td>
                    <td>
                      {(() => {
                        const eligible = applications.filter(
                          (a) =>
                            a.status !== 'draft' &&
                            a.formTemplate.id === template.id
                        );
                        const canApply = user?.userProfile?.canApply !== false;
                        if (!canApply || eligible.length === 0) {
                          return (
                            <Button
                              onClick={() => createNewApplication(template.id)}
                              variant="cta"
                              size="sm"
                              disabled={!canApply}
                            >
                              Crear postulación
                            </Button>
                          );
                        }
                        return (
                          <div className={styles.dropdownWrapper} data-dropdown>
                            <button
                              className="uc-btn btn-cta"
                              onClick={() =>
                                setOpenDropdownId(
                                  openDropdownId === template.id
                                    ? null
                                    : template.id
                                )
                              }
                              aria-expanded={openDropdownId === template.id}
                              aria-haspopup="menu"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.35rem',
                              }}
                            >
                              Crear postulación
                              <i className="uc-icon icon-size--sm">
                                expand_more
                              </i>
                            </button>
                            {openDropdownId === template.id && (
                              <div className={styles.dropdownMenu} role="menu">
                                <button
                                  className={styles.dropdownItem}
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenDropdownId(null);
                                    createNewApplication(template.id);
                                  }}
                                >
                                  Desde cero
                                </button>
                                <button
                                  className={styles.dropdownItem}
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenDropdownId(null);
                                    setSelectedSourceId('');
                                    setReuseModal({
                                      templateId: template.id,
                                    });
                                  }}
                                >
                                  Con datos de postulación anterior
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>


      <section className={styles.applicationsSection}>
        <h2 className={styles.sectionTitle}>Mis Postulaciones</h2>
        {applicationsError ? (
          <ErrorState
            title="Error al cargar postulaciones"
            message={applicationsError}
            onRetry={fetchApplications}
            retryLabel="Reintentar"
          />
        ) : applications.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyMessage}>
              No tienes postulaciones aún. Crea una nueva postulación usando los
              concursos de arriba para comenzar.
            </p>
          </div>
        ) : (
          <div className={styles.applicationsContainer}>
            {(() => {
              const now = new Date();
              type GroupEntry = {
                name: string;
                year?: number | null;
                isActive: boolean;
                deadline?: string;
                apps: typeof applications;
              };
              const grouped = applications.reduce(
                (acc, app) => {
                  const key = app.formTemplate.id || app.formTemplate.name;
                  if (!acc[key]) {
                    acc[key] = {
                      name: app.formTemplate.name,
                      year: app.formTemplate.year,
                      isActive: app.formTemplate.isActive,
                      deadline: app.formTemplate.deadline,
                      apps: [],
                    };
                  }
                  acc[key].apps.push(app);
                  return acc;
                },
                {} as Record<string, GroupEntry>
              );

              const isOpen = (g: GroupEntry) =>
                g.isActive && !!g.deadline && new Date(g.deadline) > now;

              const sorted = Object.entries(grouped).sort(([, a], [, b]) => {
                const ao = isOpen(a);
                const bo = isOpen(b);
                if (ao && !bo) return -1;
                if (!ao && bo) return 1;
                if (a.deadline && b.deadline)
                  return (
                    new Date(b.deadline).getTime() -
                    new Date(a.deadline).getTime()
                  );
                return 0;
              });

              return sorted.map(([groupId, group]) => {
                const open = isOpen(group);
                const expanded = !collapsedGroups.has(groupId);
                const count = group.apps.length;
                return (
                  <div key={groupId} className={styles.contestGroup}>
                    <div
                      className={styles.contestHeader}
                      onClick={() => toggleGroup(groupId)}
                      role="button"
                      aria-expanded={expanded}
                    >
                      <h3 className={styles.contestName}>
                        {group.name}
                        {group.year ? ` ${group.year}` : ''}
                      </h3>
                      <div className={styles.contestHeaderRight}>
                        <Badge label={open ? 'Abierto' : 'Cerrado'} variant={open ? 'success' : 'secondary'} />
                        <span className={styles.contestCount}>
                          {count}{' '}
                          {count === 1 ? 'postulación' : 'postulaciones'}
                        </span>
                        <i
                          className={`uc-icon icon-shape--rounded ${styles.contestChevron}`}
                          style={{
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                          aria-hidden="true"
                        >
                          expand_more
                        </i>
                      </div>
                    </div>

                    {expanded && (
                      <div className={styles.applicationsTable}>
                        <div className={styles.tableHeader}>
                          <div className={styles.columnHeader}>Folio</div>
                          <div className={styles.columnHeader}>
                            Título de proyecto
                          </div>
                          <div className={styles.columnHeader}>Estado</div>
                          <div
                            className={styles.columnHeader}
                            title="Fecha de creación del borrador de esta postulación"
                          >
                            {CREATION_LABEL}
                          </div>
                          <div
                            className={styles.columnHeader}
                            title="Si la postulación está en borrador, esta es la fecha de su última edición. Si ya fue enviada, es la fecha de envío."
                          >
                            {SUBMISSION_OR_EDIT_LABEL}
                          </div>
                          <div className={styles.columnHeader}></div>
                        </div>

                        {group.apps.map((application) => {
                          const projectTitle =
                            application.answers?.sectionAnswers
                              ?.PROJECT_INFORMATION?.projectTitle ||
                            application.answers?.sectionAnswers
                              ?.APPLICATION_INFORMATION?.projectTitle ||
                            'Sin título';
                          return (
                          <div key={application.id} className={styles.tableRow}>
                            <div
                              className={`${styles.tableCell} ${styles.folioCell}`}
                              data-label="Folio:"
                            >
                              {application.applicationNumber}
                            </div>
                            <div
                              className={`${styles.tableCell} ${styles.titleCell}`}
                              data-label="Título de proyecto:"
                            >
                              <span className={styles.projectTitle}>
                                {projectTitle}
                              </span>
                              {projectTitle.length > 90 && (
                                <span
                                  className={styles.titleTooltip}
                                  role="tooltip"
                                >
                                  {projectTitle}
                                </span>
                              )}
                            </div>
                            <div
                              className={styles.tableCell}
                              data-label="Estado:"
                            >
                              <Badge {...statusBadge(application.status)} />
                            </div>
                            <div
                              className={styles.tableCell}
                              data-label={`${CREATION_LABEL}:`}
                              title={
                                application.submittedAt
                                  ? 'Fecha de cuando se creó el borrador de esta postulación'
                                  : 'Fecha de creación de este borrador'
                              }
                            >
                              <div className={styles.dateCell}>
                                <span>
                                  {new Date(
                                    application.createdAt
                                  ).toLocaleDateString('es-ES', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  })}
                                </span>
                                <span className={styles.dateCellTime}>
                                  {new Date(
                                    application.createdAt
                                  ).toLocaleTimeString('es-ES', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                  hrs
                                </span>
                              </div>
                            </div>
                            <div
                              className={styles.tableCell}
                              data-label={`${SUBMISSION_OR_EDIT_LABEL}:`}
                              title={
                                application.submittedAt
                                  ? 'Fecha de envío de esta postulación'
                                  : 'Fecha de última modificación de este borrador'
                              }
                            >
                              <div className={styles.dateCell}>
                                <span>
                                  {new Date(
                                    application.submittedAt ||
                                      application.updatedAt
                                  ).toLocaleDateString('es-ES', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  })}
                                </span>
                                <span className={styles.dateCellTime}>
                                  {new Date(
                                    application.submittedAt ||
                                      application.updatedAt
                                  ).toLocaleTimeString('es-ES', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                  hrs
                                </span>
                              </div>
                            </div>
                            <div
                              className={styles.tableCell}
                              data-label="Acciones:"
                            >
                              <div className={styles.actionButtons}>
                                <Button
                                  variant={
                                    application.status === 'draft' &&
                                    application?.formTemplate?.isActive
                                      ? 'primary'
                                      : 'secondary'
                                  }
                                  onClick={() => {
                                    // Use current location pathname to determine where we came from
                                    const fromPath = location.pathname;
                                    navigate(
                                      getApplicationUrl(application.id),
                                      {
                                        state: { from: fromPath },
                                      }
                                    );
                                  }}
                                  size="sm"
                                  className={styles.leadAction}
                                >
                                  {application.status === 'draft'
                                    ? application?.formTemplate?.isActive
                                      ? 'Continuar'
                                      : 'Ver'
                                    : 'Ver'}
                                </Button>
                                <Button
                                  variant="text"
                                  onClick={() => downloadPdf(application.id)}
                                  className={styles.downloadButton}
                                  size="sm"
                                >
                                  <i className="uc-icon icon-size--sm">
                                    download
                                  </i>
                                  PDF
                                </Button>
                                {application.hasEvaluations &&
                                  application.awardNotifiedAt !== null &&
                                  (!application.formTemplate.isActive ||
                                    new Date(
                                      application.formTemplate.deadline || 0
                                    ) < new Date()) && (
                                    <Button
                                      variant="secondary"
                                      onClick={() =>
                                        navigate(
                                          `/applications/${application.id}/evaluations`
                                        )
                                      }
                                      className={styles.evaluationsButton}
                                      size="sm"
                                    >
                                      Evaluaciones
                                    </Button>
                                  )}
                                {application.status === 'draft' && (
                                  <Button
                                    variant="danger"
                                    onClick={() =>
                                      deleteApplication(application.id)
                                    }
                                    disabled={deleting === application.id}
                                    className={styles.deleteButton}
                                    size="sm"
                                  >
                                    <i className="uc-icon icon-size--sm icon-color--red">
                                      delete
                                    </i>
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}
      </section>

      <div className={styles.closedContestsLink}>
        <button
          className={styles.closedContestsButton}
          onClick={handleOpenClosedContests}
        >
          <span className={styles.closedContestsLabel}>
            Ver historial de concursos cerrados
          </span>
          <i className="uc-icon icon-shape--rounded" aria-hidden="true">
            arrow_forward
          </i>
        </button>
      </div>

      {showToast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}


      <Modal
        isOpen={closedContestsOpen}
        onClose={() => setClosedContestsOpen(false)}
        title="Historial de concursos cerrados"
        size="sm"
      >
        {closedTemplates.length === 0 ? (
          <p className={styles.modalNote}>
            No hay concursos cerrados registrados.
          </p>
        ) : (
          <table className={styles.closedContestsTable}>
            <thead>
              <tr>
                <th>Concurso</th>
                <th>Fecha de cierre</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {closedTemplates.map((t) => (
                <tr key={t.id}>
                  <td>
                    <strong>
                      {t.name}
                      {t.year ? ` ${t.year}` : ''}
                    </strong>
                    {t.description && (
                      <p className={styles.closedContestsDesc}>
                        {t.description}
                      </p>
                    )}
                  </td>
                  <td className={styles.closedContestsDate}>
                    {t.deadline
                      ? new Date(t.deadline).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className={styles.closedContestsActions}>
                    <div style={{ display: 'flex', gap: 'var(--spacing-8)', flexWrap: 'wrap' }}>
                      <button
                        className={styles.downloadLink}
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = '/templates/Bases_Avanza_UC.pdf';
                          link.download = 'Bases_Avanza_UC.pdf';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        <i className="uc-icon icon-size--sm">download</i>
                        Bases en Español
                      </button>
                      <button
                        className={styles.downloadLink}
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = '/templates/Bases_Avanza_UC_EN.pdf';
                          link.download = 'Bases_Avanza_UC_EN.pdf';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        <i className="uc-icon icon-size--sm">download</i>
                        Bases en Inglés
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>

      {reuseModal &&
        (() => {
          const eligible = applications.filter((a) => a.status !== 'draft');
          return (
            <Modal
              isOpen={!!reuseModal}
              onClose={() => setReuseModal(null)}
              title="Iniciar postulación con datos anteriores"
              size="md"
              footer={
                <>
                  <Button
                    variant="text"
                    onClick={() => {
                      setReuseModal(null);
                      createNewApplication(reuseModal.templateId);
                    }}
                  >
                    Crear sin datos anteriores
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleCreateFromSource}
                    disabled={!selectedSourceId}
                  >
                    Iniciar postulación
                  </Button>
                </>
              }
            >
              <div className={styles.reuseInfoBox}>
                Los datos de la postulación seleccionada se copiarán como
                punto de partida y podrás editar cualquier campo antes de
                enviar. Ten en cuenta que si las versiones del concurso
                tienen secciones o campos distintos, algunos datos podrían
                no trasladarse o aparecer en blanco — revisa cada sección
                antes de enviar.
              </div>

              {eligible.length === 0 ? (
                <p className={styles.reuseEmpty}>
                  No tienes postulaciones anteriores para reutilizar.
                </p>
              ) : (
                <ul className={styles.reuseAppList}>
                  {eligible.map((app) => {
                    const title =
                      app.answers?.sectionAnswers?.PROJECT_INFORMATION
                        ?.projectTitle ||
                      app.answers?.sectionAnswers?.APPLICATION_INFORMATION
                        ?.projectTitle ||
                      'Sin título';
                    const isSelected = selectedSourceId === app.id;
                    return (
                      <li
                        key={app.id}
                        className={`${styles.reuseAppItem} ${isSelected ? styles.reuseAppItemSelected : ''}`}
                        onClick={() => setSelectedSourceId(app.id)}
                      >
                        <input
                          type="radio"
                          name="sourceApp"
                          value={app.id}
                          checked={isSelected}
                          onChange={() => setSelectedSourceId(app.id)}
                          className={styles.reuseAppRadio}
                        />
                        <div className={styles.reuseAppInfo}>
                          <span className={styles.reuseAppTitle}>
                            {title}
                          </span>
                          <span className={styles.reuseAppMeta}>
                            {app.formTemplate.name}
                            {app.formTemplate.year
                              ? ` ${app.formTemplate.year}`
                              : ''}{' '}
                            · {app.applicationNumber}
                          </span>
                        </div>
                        <Badge {...statusBadge(app.status)} />
                      </li>
                    );
                  })}
                </ul>
              )}
            </Modal>
          );
        })()}
    </div>
  );
};

export default DynamicApplicationList;
